import { useCallback, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { useRegisterDomainMutation, useValidateDomainMutation } from 'src/store/services/project.service';

export interface UseDomainVerificationResult {
  isDomainValid: boolean;
  isDomainDnsVerified: boolean;
  isDomainVerified: boolean;
  isLoading: boolean;
  verifyDomainData?: any;
  verifyAndCreateDomain: (showModal?: () => void) => Promise<boolean>;
  verifyDomain: () => Promise<boolean>;
}

export function useDomainVerification(
  methods: UseFormReturn<any>,
  address: string | undefined,
): UseDomainVerificationResult {
  const [isDomainValid, setIsDomainValid] = useState(false);
  const [isDomainDnsVerified, setIsDomainDnsVerified] = useState(false);
  const [isDomainVerified, setIsDomainVerified] = useState(false);

  const [registerDomain, { isLoading: isRegisteringDomain, data: registerDomainData }] = useRegisterDomainMutation();
  const [validateDomain, { isLoading: isValidatingDomain, data: validateDomainData }] = useValidateDomainMutation();

  const verifyDomainData = useMemo(
    () => registerDomainData?.data ?? validateDomainData?.data,
    [registerDomainData, validateDomainData],
  );

  const getDomainFromForm = useCallback((): string | undefined => {
    const formValues = methods.getValues();
    return (formValues as any)?.info?.dns?.host || (formValues as any)?.info?.nodeSaleUrl;
  }, [methods]);

  const verifyAndCreateDomain = useCallback(
    async (showModal?: () => void): Promise<boolean> => {
      const domainToVerify = getDomainFromForm();
      if (!domainToVerify || !address) return true;

      try {
        const verifyRes: any = await validateDomain({ dns: domainToVerify, address }).unwrap();
        let isValid = !!verifyRes?.isValid;
        let isVerified = !!verifyRes?.data?.verified;
        let isDnsVerified = !!verifyRes?.isDomainVerified;

        setIsDomainValid(isValid);
        setIsDomainVerified(isVerified);
        setIsDomainDnsVerified(isDnsVerified);

        if (!isValid && !verifyRes?.data?.name) {
          await registerDomain({ dns: domainToVerify, address }).unwrap();
          const registerRes: any = await validateDomain({ dns: domainToVerify, address }).unwrap();
          isValid = !!registerRes?.isValid;
          isVerified = !!registerRes?.data?.verified;
          isDnsVerified = !!registerRes?.isDomainVerified;

          setIsDomainValid(isValid);
          setIsDomainVerified(isVerified);
          setIsDomainDnsVerified(isDnsVerified);
        }

        if (!isVerified || !isValid || !isDnsVerified) {
          showModal?.();
          return false;
        }
        return true;
      } catch (_e) {
        return false;
      }
    },
    [address, getDomainFromForm, registerDomain, validateDomain],
  );

  const verifyDomain = useCallback(async (): Promise<boolean> => {
    const domainToVerify = getDomainFromForm();
    if (!domainToVerify || !address) return false;
    try {
      const verifyRes: any = await validateDomain({ dns: domainToVerify, address }).unwrap();
      const isValid = !!verifyRes?.isValid;
      const isVerified = !!verifyRes?.data?.verified;
      const isDnsVerified = !!verifyRes?.isDomainVerified;

      setIsDomainValid(isValid);
      setIsDomainVerified(isVerified);
      setIsDomainDnsVerified(isDnsVerified);

      return isValid && isVerified && isDnsVerified;
    } catch (_e) {
      return false;
    }
  }, [address, getDomainFromForm, validateDomain]);

  return {
    isDomainValid,
    isDomainDnsVerified,
    isDomainVerified,
    isLoading: isRegisteringDomain || isValidatingDomain,
    verifyDomainData,
    verifyAndCreateDomain: verifyAndCreateDomain,
    verifyDomain: verifyDomain,
  };
}
